/*
* Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*  SPDX-License-Identifier: Apache-2.0
*/

import type { Offer } from '../models/catalog-dto';

export interface PolicySummaryLabels {
    timeRange: (start: string, end: string) => string;
    timeFrom: (date: string) => string;
    timeUntil: (date: string) => string;
    locationIsAnyOf: (values: string) => string;
    locationEq: (value: string) => string;
    generic: (left: string, op: string, right: string) => string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return null;
}

function operandLabel(value: unknown): string {
    if (typeof value === 'string') {
        return value.replace(/^(edc:|odrl:)/, '');
    }
    const o = asRecord(value);
    if (o && '@id' in o) {
        return String(o['@id']).replace(/^(https?:\/\/[^#]*#|.*\/)/, '').replace(/^(edc:|odrl:)/, '');
    }
    return '';
}

function normalizeLeftOperand(value: unknown): string {
    const label = operandLabel(value);
    if (!label) {
        return '';
    }
    const simple = label.replace(/^edc:/, '').replace(/^odrl:/, '');
    if (simple === 'timeInterval' || simple.endsWith('timeInterval')) {
        return 'timeInterval';
    }
    if (simple === 'location' || simple.endsWith('location')) {
        return 'location';
    }
    return simple;
}

function normalizeOperator(value: unknown): string {
    return operandLabel(value).replace(/^odrl:/, '').toLowerCase();
}

export function formatPolicyDate(iso: string, locale: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return iso;
    }
    try {
        return new Intl.DateTimeFormat(locale || 'es', {
            dateStyle: 'medium',
            timeStyle: 'short'
        }).format(d);
    } catch {
        return d.toISOString().slice(0, 16).replace('T', ' ');
    }
}

function valuesFromJsonLdValueNode(node: unknown): string[] {
    const out: string[] = [];
    const rec = asRecord(node);
    if (!rec) {
        return out;
    }
    const v = rec['@value'];
    if (typeof v === 'string') {
        out.push(v.trim());
        return out;
    }
    const inner = asRecord(v);
    if (inner && typeof inner['string'] === 'string') {
        out.push(String(inner['string']).trim());
    }
    return out;
}

function extractLocationValues(right: unknown): string[] {
    if (right == null) {
        return [];
    }
    if (typeof right === 'number' || typeof right === 'boolean') {
        return [String(right)];
    }
    if (typeof right === 'string') {
        const s = right.trim();
        if (!s) {
            return [];
        }
        if ((s.startsWith('[') || s.startsWith('{')) && !s.includes('@type=')) {
            try {
                const parsed = JSON.parse(s) as unknown;
                return extractLocationValues(parsed);
            } catch {
                /* seguir con heurísticas */
            }
        }
        const fromPatterns = new Set<string>();
        for (const m of s.matchAll(/string=([^\s,}\]]+)/g)) {
            const v = m[1].trim();
            if (
                v &&
                !v.includes('http') &&
                !v.includes('www.w3.org') &&
                v !== 'STRING' &&
                v !== 'valueType'
            ) {
                fromPatterns.add(v);
            }
        }
        if (fromPatterns.size) {
            return [...fromPatterns];
        }
        for (const m of s.matchAll(/\bchars=([a-zA-Z]{2,8})(?=,|\}|])/g)) {
            fromPatterns.add(m[1]);
        }
        if (fromPatterns.size) {
            return [...fromPatterns];
        }
        if (s.length <= 64 && !s.includes('{') && !s.includes('[')) {
            return [s];
        }
        return [];
    }
    if (Array.isArray(right)) {
        const nested = right.flatMap((item) => extractLocationValues(item));
        return [...new Set(nested.filter(Boolean))];
    }
    const rec = asRecord(right);
    if (rec && '@list' in rec && Array.isArray(rec['@list'])) {
        return (rec['@list'] as unknown[]).flatMap((item) => extractLocationValues(item));
    }
    if (rec && '@value' in rec) {
        return valuesFromJsonLdValueNode(right);
    }
    return [];
}

function formatGenericRight(value: unknown, maxLen: number): string {
    if (value === undefined || value === null) {
        return '';
    }
    if (typeof value === 'string') {
        const vals = extractLocationValues(value);
        if (vals.length) {
            const joined = vals.join(', ');
            return joined.length > maxLen ? `${joined.slice(0, maxLen - 1)}…` : joined;
        }
        return value.length > maxLen ? `${value.slice(0, maxLen - 1)}…` : value;
    }
    try {
        const s = JSON.stringify(value);
        return s.length > maxLen ? `${s.slice(0, maxLen - 1)}…` : s;
    } catch {
        return String(value);
    }
}

function operatorWord(op: string): string {
    const map: Record<string, string> = {
        gteq: '≥',
        lteq: '≤',
        gt: '>',
        lt: '<',
        eq: '=',
        neq: '≠',
        isanyof: '∈',
        isoneof: '∈',
        isallof: '⊆'
    };
    return map[op.toLowerCase()] ?? op;
}

interface ParsedConstraint {
    left: string;
    op: string;
    right: unknown;
}

function parseConstraint(c: Record<string, unknown>): ParsedConstraint {
    return {
        left: normalizeLeftOperand(c['odrl:leftOperand']),
        op: normalizeOperator(c['odrl:operator']),
        right: c['odrl:rightOperand']
    };
}

function permissionsList(permission: unknown): Record<string, unknown>[] {
    if (permission == null) {
        return [];
    }
    if (Array.isArray(permission)) {
        return permission.filter((p): p is Record<string, unknown> => asRecord(p) != null);
    }
    const one = asRecord(permission);
    return one ? [one] : [];
}

function constraintsList(constraint: unknown): Record<string, unknown>[] {
    if (constraint == null) {
        return [];
    }
    if (Array.isArray(constraint)) {
        return constraint.filter((c): c is Record<string, unknown> => asRecord(c) != null);
    }
    const one = asRecord(constraint);
    return one ? [one] : [];
}

function humanizeConstraints(
    rawCons: Record<string, unknown>[],
    labels: PolicySummaryLabels,
    locale: string,
    maxRightLen: number
): string[] {
    const parsed = rawCons.map((r) => parseConstraint(r));
    const lines: string[] = [];
    const used = new Set<number>();

    const gteIdx = parsed.findIndex(
        (p) => p.left === 'timeInterval' && (p.op === 'gteq' || p.op === 'gt')
    );
    const lteIdx = parsed.findIndex(
        (p) => p.left === 'timeInterval' && (p.op === 'lteq' || p.op === 'lt')
    );

    if (gteIdx >= 0 && lteIdx >= 0) {
        const startRaw = String(parsed[gteIdx].right ?? '');
        const endRaw = String(parsed[lteIdx].right ?? '');
        lines.push(
            labels.timeRange(formatPolicyDate(startRaw, locale), formatPolicyDate(endRaw, locale))
        );
        used.add(gteIdx);
        used.add(lteIdx);
    } else if (gteIdx >= 0) {
        lines.push(labels.timeFrom(formatPolicyDate(String(parsed[gteIdx].right ?? ''), locale)));
        used.add(gteIdx);
    } else if (lteIdx >= 0) {
        lines.push(labels.timeUntil(formatPolicyDate(String(parsed[lteIdx].right ?? ''), locale)));
        used.add(lteIdx);
    }

    parsed.forEach((p, i) => {
        if (used.has(i) || p.left !== 'location') {
            return;
        }
        const vals = extractLocationValues(p.right);
        const joined = vals.join(', ');
        if (p.op === 'isanyof' || p.op === 'isoneof') {
            lines.push(labels.locationIsAnyOf(joined || formatGenericRight(p.right, maxRightLen)));
        } else if (p.op === 'eq') {
            lines.push(labels.locationEq(joined || formatGenericRight(p.right, maxRightLen)));
        } else {
            const right = joined || formatGenericRight(p.right, maxRightLen);
            lines.push(labels.generic(p.left || 'location', operatorWord(p.op), right));
        }
        used.add(i);
    });

    parsed.forEach((p, i) => {
        if (used.has(i)) {
            return;
        }
        const left = p.left || operandLabel(rawCons[i]['odrl:leftOperand']) || '?';
        const op = operatorWord(p.op);
        const right = formatGenericRight(p.right, maxRightLen);
        if (!left && !op && !right) {
            return;
        }
        lines.push(labels.generic(left, op, right));
    });

    return lines.filter(Boolean);
}

function collectHumanizedLines(
    offer: Offer,
    labels: PolicySummaryLabels,
    locale: string,
    maxRightLen: number
): string[] {
    const lines: string[] = [];
    const rawPerm = offer['odrl:permission'];
    if (rawPerm == null) {
        return lines;
    }
    if (Array.isArray(rawPerm) && rawPerm.length === 0) {
        return lines;
    }
    for (const perm of permissionsList(rawPerm)) {
        const cons = constraintsList(perm['odrl:constraint']);
        if (!cons.length) {
            continue;
        }
        lines.push(...humanizeConstraints(cons, labels, locale, maxRightLen));
    }
    return lines;
}

const defaultLabels: PolicySummaryLabels = {
    timeRange: (start, end) => `${start} – ${end}`,
    timeFrom: (date) => `${date}`,
    timeUntil: (date) => `${date}`,
    locationIsAnyOf: (values) => `Location: ${values}`,
    locationEq: (value) => `Location: ${value}`,
    generic: (left, op, right) => [left, op, right].filter(Boolean).join(' ')
};

export interface SummarizePolicyOptions {
    maxLength?: number;
    maxRightOperandLength?: number;
    locale?: string;
    labels?: PolicySummaryLabels;
}

export interface OfferHumanSummary {
    index: number;
    offerId: string;
    lines: string[];
}

export function summarizeOffersHuman(
    offers: Offer[],
    options?: SummarizePolicyOptions
): OfferHumanSummary[] {
    const maxRight = options?.maxRightOperandLength ?? 400;
    const locale = options?.locale ?? 'es';
    const labels = options?.labels ?? defaultLabels;

    return offers.map((offer, index) => {
        const lines = collectHumanizedLines(offer, labels, locale, maxRight);
        const rawId = offer['@id'];
        const offerId = typeof rawId === 'string' ? rawId : '';
        return { index, offerId, lines };
    });
}

export function summarizePolicyConstraints(
    offers: Offer[],
    options?: SummarizePolicyOptions
): string {
    const maxRight = options?.maxRightOperandLength ?? 200;
    const locale = options?.locale ?? 'es';
    const labels = options?.labels ?? defaultLabels;

    if (!offers.length) {
        return '';
    }
    const segments: string[] = [];
    offers.forEach((offer, idx) => {
        const lines = collectHumanizedLines(offer, labels, locale, maxRight);
        if (!lines.length) {
            return;
        }
        const prefix = offers.length > 1 ? `[${idx + 1}] ` : '';
        segments.push(prefix + lines.join('; '));
    });
    if (!segments.length) {
        return '';
    }
    let result = segments.join(' · ');
    const max = options?.maxLength;
    if (max != null && result.length > max) {
        result = `${result.slice(0, max - 1)}…`;
    }
    return result;
}